/**
 * Convert HSL color values to hex format
 * @param h Hue (0-360)
 * @param s Saturation (0-100)
 * @param l Lightness (0-100)
 * @returns Hex color string (e.g., "#1f1f1f")
 */
export function hslToHex(h: number, s: number, l: number): string {
    // Convert percentages to decimals
    s = s / 100;
    l = l / 100;

    const c = (1 - Math.abs(2 * l - 1)) * s;
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
    const m = l - c / 2;

    let r = 0, g = 0, b = 0;

    if (0 <= h && h < 60) {
        r = c; g = x; b = 0;
    } else if (60 <= h && h < 120) {
        r = x; g = c; b = 0;
    } else if (120 <= h && h < 180) {
        r = 0; g = c; b = x;
    } else if (180 <= h && h < 240) {
        r = 0; g = x; b = c;
    } else if (240 <= h && h < 300) {
        r = x; g = 0; b = c;
    } else if (300 <= h && h < 360) {
        r = c; g = 0; b = x;
    }

    // Convert to 0-255 range and round
    r = Math.round((r + m) * 255);
    g = Math.round((g + m) * 255);
    b = Math.round((b + m) * 255);

    // Convert to hex
    const toHex = (n: number) => n.toString(16).padStart(2, '0');
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/**
 * Parse HSL string and convert to hex
 * Supports formats like "0 0% 12.16%" or "hsl(0, 0%, 12.16%)"
 * @param hslString HSL string
 * @returns Hex color string
 */
export function parseHslToHex(hslString: string): string {
    // Remove "hsl(" and ")" if present, and clean up the string
    const cleaned = hslString.replace(/hsl\(|\)/g, '').trim();

    // Split by comma or space, depending on format
    const parts = cleaned.includes(',')
        ? cleaned.split(',').map(s => s.trim())
        : cleaned.split(/\s+/);

    if (parts.length !== 3) {
        console.warn('Invalid HSL format:', hslString);
        return '#000000'; // fallback to black
    }

    // Parse hue (number)
    const h = parseFloat(parts[0]);

    // Parse saturation (remove % and convert to number)
    const s = parseFloat(parts[1].replace('%', ''));

    // Parse lightness (remove % and convert to number)
    const l = parseFloat(parts[2].replace('%', ''));

    return hslToHex(h, s, l);
} 